import TileLayer from "ol/layer/Tile";
import { BingMaps, TileWMS, XYZ } from "ol/source";
import { getImageScale } from "./helpers";
import { Feature } from "ol";
import { getStyleForBasicFeature } from "./styles/mapStyles";
import VectorLayer from "ol/layer/Vector";
import VectorSource from "ol/source/Vector";
import { Point } from "ol/geom";
import { Icon, Style } from "ol/style";
import GeoJSON from "ol/format/GeoJSON";
import WKT from "ol/format/WKT";
import { getItemWebStorage } from "../../../features/webStorageSaver";
import { store } from "../../../stateManagement/store";
import { rosreestrLayersSettings } from "./mapFunctions/rosreestrFunctions/rosreestrFunctions";
import { IndorImages, getIndorImageSrc } from "../../../features";

// Основная подложка карты
export function getRasterLayer(highContrastModeIsEnabled: boolean) {
    return new TileLayer({
        preload: Infinity,
        zIndex: 0,
        className: highContrastModeIsEnabled ? "high-contrast-mode" : "",
        source: new XYZ({
            url: 'https://map.indorsoft.ru/hot/{z}/{x}/{y}.png',
            tilePixelRatio: getImageScale().imageScale,
            maxZoom: 20
        }),
    });
}

// Основной слой дорожной сети
export function getAllRoadObjectsLayer(env?: string) {
    const currentConfig = getItemWebStorage('currentConfig')
    const { wmsServer, workspace, mainLayer } = currentConfig ?? {}
    return new TileLayer({
        preload: Infinity,
        source: new TileWMS({
            serverType: 'geoserver',
            url: wmsServer + '/wms',
            params: {
                'LAYERS': `${workspace}:${mainLayer}`, // Указать нужные слои
                'TILED': 'true',
                'FORMAT': 'image/png',
                'SRS': 'EPSG:3857',
                'dpi': getImageScale().dpi?.toString(),
                'env': env
            },
        })
    });
}

// Обычный слой для типа объекта
export function getBasicObjectTypeLayer(layers: any, wmsServer: string, wmsVersion: string, workspace: string, layerFilter?: any, styles?: string) {
    return new TileLayer({
        preload: Infinity,
        source: new TileWMS({
            serverType: 'geoserver',
            url: wmsServer + 'wms',
            params: {
                'VERSION': wmsVersion,
                'LAYERS': Array.isArray(layers) ? `${workspace}:${layers.join(',')}` : (workspace + ':' + layers),
                'TILED': true,
                'FORMAT': 'image/png',
                'SRS': 'EPSG:3857',
                'CQL_FILTER': layerFilter || null,
                'STYLES': styles || null,
                'dpi': getImageScale().dpi?.toString()
            },
        })
    })
}

// Источник с аэросъемкой
export function getAeroSource(aeroLayerUrl: any) {
    return new XYZ({
        tilePixelRatio: getImageScale().imageScale,
        projection: 'EPSG:3857',
        tileUrlFunction: (tileCoord) => {
            const z = tileCoord[0];
            const x = tileCoord[1];
            const y = -tileCoord[2];
            function newAeroLink(x: any, y: any, pyramidLevel?: any) {
                if (pyramidLevel === undefined) pyramidLevel = 0;
                let path = '';
                const power = Math.pow(100, pyramidLevel);
                const px = Math.floor(Math.abs(x) / power);
                const py = Math.floor(Math.abs(y) / power);
                if (px >= 100 || py >= 100) path = newAeroLink(x, y, pyramidLevel + 1);
                if (pyramidLevel > 0 && px + py > 0) {
                    let xx = '';
                    let yy = '';
                    for (let i = 0; i < pyramidLevel; i++) {
                        xx += 'xx';
                        yy += 'yy';
                    }
                    path = path + px + xx + '-' + py + yy + '/';
                }
                return path;
            }
            const pyramidFolderName = newAeroLink(x, y);
            const path = z + '/' + pyramidFolderName + 'tile-' + x + '' + y + '.png';
            return aeroLayerUrl + path;
        },
    });
}

// Источник в XYZ
export function getDefaultXYZSource(layerUrl: any) {
    return new XYZ({
        url: layerUrl,
        tilePixelRatio: getImageScale().imageScale
    })
}

// Слой аэрофотосъёмка «Индор-Авиа»
export function getAeroLayer() {
    const aeroLayerUrl = 'https://geo.indorsoft.ru/rasters/layer.aero/'
    return new TileLayer({
        source: getAeroSource(aeroLayerUrl),
        visible: true,
    });
}

// Слой спутник Bing
export function getBingLayer() {
    return new TileLayer({
        visible: true,
        preload: Infinity,
        maxZoom: 19,
        source: new BingMaps({
            key: 'AsDnXUG1rclVvkhHRQopA6Yk0O7FK1zqwzH4tmeif3AZcKcUV1Trb9L-2XVsVt8c',
            imagerySet: 'Aerial',
        })
    })
}

// Дополнительные уникальные слои для базы
export function applyAdditionalMapLayers(additionalLayersDisplayState: any, currentConfig: any, layersArray?: any) {
    Object.keys(additionalLayersDisplayState).map((key) => {
        if (additionalLayersDisplayState[key]) {
            let layerObject = currentConfig?.mapAdditionalLayers?.layers.find((layer: any) => layer?.description?.id === key)
            let layerSource
            if (layerObject?.description.type === 'XYZ') {
                layerSource = getDefaultXYZSource(layerObject?.description.url)
            }
            if (layerObject?.description.type === 'Aero') {
                layerSource = getAeroSource(layerObject?.description.url)
            }
            const additionalLayer = new TileLayer({
                source: layerSource,
                visible: true,
            });
            if (layersArray) layersArray.push(additionalLayer)
        }
    })
    return layersArray
}

// Основной слой дорожной сети
export function getRosreestrLayers() {
    const rosreestrLayersDisplayState: any = store.getState().mapSettings.rosreestrLayersDisplayState
    let newRosreestrLayers: any = []
    if (rosreestrLayersDisplayState) {
        const rosreestrLayersDisplayStateActiveItems = Object.keys(rosreestrLayersDisplayState).filter((layerIndex: any) => rosreestrLayersDisplayState[layerIndex] && !layerIndex.includes('Labels'))
        let currentActiveRosreestrLayersIndexes: any = rosreestrLayersDisplayStateActiveItems.map((layerIndex: any) => rosreestrLayersSettings[layerIndex]?.name)
        currentActiveRosreestrLayersIndexes.map((layerName: any) => {
            const rosreestrLayer = new TileLayer({
                preload: Infinity,
                source: new TileWMS({
                    serverType: 'geoserver',
                    url: `https://nspd.gov.ru/api/aeggis/v4/${layerName}/wms`,
                    params: {
                        'VERSION': '1.3.0',
                        'LAYERS': layerName,
                        'TILED': 'true',
                        'FORMAT': 'image/png',
                        'CRS': 'EPSG:3857',
                        'dpi': getImageScale().dpi?.toString()
                    },
                })
            });
            newRosreestrLayers.push(rosreestrLayer)
        })
    }
    return newRosreestrLayers
}

export function getGismeteoPrecipitationLayer(timeMarkIndex: number, time: string) {
    // Убрать миллисекунды
    let currentTimeWithoutMilliseconds = time.slice(0, 19);
    return new TileLayer({
        preload: Infinity,
        zIndex: 2,
        source: new XYZ({
            tileUrlFunction: function ([z, x, y]) {
                return `https://api.gismeteo.ng/v3/tile-generator/maps/precipitation/nowcast/${z}/${timeMarkIndex}/${x}x${y}.png?${currentTimeWithoutMilliseconds}`;
            }
        }),
        opacity: 0.8
    });
}

// Основной слой дорожной сети
export function getRosreestrLabelsLayers() {
    const rosreestrLayersDisplayState: any = store.getState().mapSettings.rosreestrLayersDisplayState
    let rosreestrLabelsLayers: any = []
    if (rosreestrLayersDisplayState) {
        const transformToLayerIndex = (labelsLayerIndex: string) => labelsLayerIndex.replace('Labels', '')
        const rosreestrLabelsLayersActiveItemsFilter = (layerIndex: any) => rosreestrLayersDisplayState[layerIndex] && layerIndex.includes('Labels')
            && rosreestrLayersDisplayState[transformToLayerIndex(layerIndex)]
        const rosreestrLabelsLayersDisplayStateActiveItems = Object.keys(rosreestrLayersDisplayState).filter(rosreestrLabelsLayersActiveItemsFilter)
        let currentActiveRosreestrLabelsLayersIndexes: any = rosreestrLabelsLayersDisplayStateActiveItems.map((labelsLayerIndex: any) => rosreestrLayersSettings[transformToLayerIndex(labelsLayerIndex)]?.labelsName)
        currentActiveRosreestrLabelsLayersIndexes.map((labelsLayerName: any) => {
            const rosreestrLabelLayer = new TileLayer({
                preload: Infinity,
                source: new TileWMS({
                    serverType: 'geoserver',
                    url: `https://nspd.gov.ru/api/aeggis/v4/${labelsLayerName}/wms`,
                    params: {
                        'VERSION': '1.3.0',
                        'LAYERS': labelsLayerName,
                        'TILED': 'true',
                        'FORMAT': 'image/png',
                        'CRS': 'EPSG:3857',
                        'dpi': getImageScale().dpi?.toString()
                    },
                })
            });
            rosreestrLabelsLayers.push(rosreestrLabelLayer)
        })
    }
    return rosreestrLabelsLayers
}

// Универсальный контроллер загрузки пропущенных тайлов
export const reloadController: any = {
    queue: {},						// Очередь неудачных по которым отправлены повторные запросы
    attemptsLoadNumber: 5,			// Количество попыток загрузки на 1 тайл
    attemptsDelay: 1000,
    counter: 1,	// Задержка мс перед повторной попыткой (можно 0)
    onTileLoadError: function (event: any, showRosreestrAlerts: any) {
        // Идентификатор тайла на основе координат
        let uid = (event && event.tile.tileCoord) ? event.tile.tileCoord.join(".") : undefined;
        showRosreestrAlerts(this.counter++)
        // Для нового тайла с ошибкой заведём место в массиве проблемных
        if (!reloadController.queue[uid])
            reloadController.queue[uid] = 1;	// Один раз он уже дал сбой
        // Если разрешено ещё сделать попытку - пробуем
        if (reloadController.queue[uid] <= reloadController.attemptsLoadNumber) {
            // Запланировать следующую попытку
            setTimeout(
                function () {
                    // Увеличить значение счётчика загрузок
                    reloadController.queue[uid]++;
                    console.warn("Повторная загрузка...");
                    event.tile.load();
                },
                reloadController.attemptsDelay
            )
        } else {
            // Попыток не осталось. Превышено число попыток загрузки тайла.
            // Вывести сообщение об ошибке.
            console.error("Превышено число попыток загрузки слоя...");
        }
    }
}

export function getGeoMarkerLayer() {
    const geoMarkerImg = getIndorImageSrc(IndorImages.Geomarker, 20);
    return new VectorLayer({
        zIndex: 1000,
        source: new VectorSource({
            features: [
                new Feature({
                    geometry: new Point([0, 0])
                })
            ]
        }),
        style: new Style({
            image: new Icon({ src: geoMarkerImg }),
        })
    });
}

export function getGeoJSONFeaturesVectorLayer(geoJSON: any, featureProjection: string, style?: any) {
    let features: any = []
    if (geoJSON) {
        features = new GeoJSON().readFeatures(geoJSON, {
            featureProjection: featureProjection,
        })
    }
    let vectorSource = new VectorSource({
        features: features
    });
    return new VectorLayer({
        source: vectorSource,
        style: style ?? getStyleForBasicFeature,
    });
}

export function getGeoJSONFeaturesVectorLayers(geoJSONArray: any, featureProjection: string, style?: any) {
    let vectorLayers = []
    for (const geoJSON of geoJSONArray) {
        let features: any = []
        if (geoJSON) {
            features = new GeoJSON().readFeatures(geoJSON, {
                featureProjection: featureProjection,
            })
        }
        let vectorSource = new VectorSource({
            features: features
        });
        const vectorLayer = new VectorLayer({
            source: vectorSource,
            style: style ?? getStyleForBasicFeature,
        });
        vectorLayers.push(vectorLayer)
    }
    return vectorLayers
}

export function getWKTFeatureVectorLayer(wkt: any, style?: any) {
    let features: any = []
    if (wkt && Array.isArray(wkt)) {
        wkt.map(wktString => {
            const feature = new WKT().readFeature(wktString, {
                dataProjection: 'EPSG:4326',
                featureProjection: 'EPSG:3857',
            });
            features.push(feature)
        })
    }
    if (wkt && !Array.isArray(wkt)) {
        features = new WKT().readFeatures(wkt, {
            dataProjection: 'EPSG:4326',
            featureProjection: 'EPSG:3857',
        })
    }
    const vectorSource = new VectorSource({
        features: features
    });
    return new VectorLayer({
        source: vectorSource,
        style: style ?? getStyleForBasicFeature,
    });
}

export function getPanoramaVideoLayer(wmsServer: string, wmsVersion: string, workspace: string, layerFilter?: string) {
    return new TileLayer({
        preload: Infinity,
        source: new TileWMS({
            serverType: 'geoserver',
            url: wmsServer + '/wms',
            params: {
                'SERVICE': 'WMS',
                'VERSION': wmsVersion,
                'REQUEST': 'GetMap',
                'FORMAT': 'image/png',
                'TRANSPARENT': true,
                'LAYERS': workspace + ':' + "MARoadVideoPosition",
                'TILED': true,
                'STYLES': '',
                'SRS': 'EPSG:3857',
                'dpi': getImageScale().dpi.toString(),
                'CQL_FILTER': layerFilter,
            },
        })
    })
}

export function getCameraMarkerLayer(position: any, style: any) {
    let feature = new Feature({
        geometry: new Point(position),
    })
    return new VectorLayer({
        zIndex: 10000,
        source: new VectorSource({
            features: [feature],
        }),
        style: style
    })
}